import {
  OptionType,
  OptionVisibility,
  type OptionEntry,
  type SchemaVariant,
} from '@morph-mapper/types';
import {
  LOGIC_BLOCKS_RULESET,
  type BlockRule,
  type LogicBlockIndex,
  type LogicBlockRecord,
} from '@morph-mapper/node-logic';
import type { SuggestedGraph } from '@/store';
import type { Graph, NodeInstance } from '../../../graph-editor/types';
import { TERMINAL_NODE_ID } from '../../../graph-editor/config/graph';
import { createNodeInstance } from '../../../graph-editor/lib/node';
import { defaultIdFactory } from '../../../graph-editor/lib/commands/utils';
import type { ReplaceGraphCommandProps } from '../../../graph-editor/lib/commands';

/** The node types of the template's file type. */
export type Blocks = LogicBlockRecord<any, Set<BlockRule>>;

/** The node types of a file type; none before a template is loaded. */
export const blocksFor = (variant: SchemaVariant | undefined): Blocks =>
  variant ? (LOGIC_BLOCKS_RULESET[variant] as Blocks) : {};
type Block = Blocks[string];
type Port = { id: string; multi?: boolean };
type SuggestedNode = SuggestedGraph['nodes'][number];

/** Where an edge goes when it gives the field its value. */
const OUTPUT = 'output';

/** Why the assistant's graph can't be built, and the node (and option) it is about. */
export type GraphProblem = {
  problem:
    | 'unknownKind'
    | 'unknownOption'
    | 'badOption'
    | 'duplicateNode'
    | 'unknownNode'
    | 'unknownPort'
    | 'twoParents'
    | 'inputTaken'
    | 'noOutput'
    | 'notConnected';
  nodeId?: string;
  option?: string;
};

const inputsOf = (block: Block): readonly Port[] => block.ports?.input ?? [];
const outputOf = (block: Block): string | undefined =>
  block.ports?.output?.[0]?.id;

/** The input an edge goes into: the one it names, or else the only one. */
const inputFor = (block: Block, port?: string): Port | undefined => {
  const inputs = inputsOf(block);
  if (port) return inputs.find((input) => input.id === port);
  return inputs.length === 1 ? inputs[0] : undefined;
};

/** The node types the assistant may use: all but the editor's own. */
const blockFor = (blocks: Blocks, kind: string): Block | undefined => {
  const block = blocks[kind];
  return block && block.category !== 'internal' ? block : undefined;
};

type Schema = {
  safeParse: (value: unknown) => { success: boolean };
  _def?: Record<string, unknown>;
};

/** The options of a list option's type, looking through optional and default. */
const listOptions = (schema: Schema | undefined): string[] => {
  const def = schema?._def;
  switch (def?.typeName) {
    case 'ZodOptional':
    case 'ZodNullable':
    case 'ZodDefault':
      return listOptions(def.innerType as Schema);
    case 'ZodEnum':
      return def.values as string[];
    case 'ZodNativeEnum':
      return Object.values(def.values as object).filter(
        (v): v is string => typeof v === 'string',
      );
    default:
      return [];
  }
};

/** Other ways to read a value, the way the assistant may have written it. */
const readings = (value: unknown, schema: Schema): unknown[] => {
  if (typeof value === 'number' || typeof value === 'boolean')
    return [String(value)];
  if (typeof value !== 'string') return [];
  const text = value.trim();
  const found: unknown[] = [text, [text]];
  if (text !== '' && !Number.isNaN(Number(text))) found.push(Number(text));
  if (/^(true|false)$/i.test(text)) found.push(text.toLowerCase() === 'true');
  found.push(
    ...listOptions(schema).filter(
      (option) => option.toLowerCase() === text.toLowerCase(),
    ),
  );
  return found;
};

/**
 * An option's value as the editor would store it: as given when it fits the
 * option's type, or else read another way, like a number or yes/no written as
 * text, a list option in another case, or one word for a list of words.
 * Undefined when it doesn't fit at all.
 */
export const optionValue = (
  config: { type: unknown },
  value: unknown,
): { value: unknown } | undefined => {
  const schema = config.type as Schema;
  const candidates = [value, ...readings(value, schema)];
  const index = candidates.findIndex(
    (candidate) => schema.safeParse(candidate).success,
  );
  return index === -1 ? undefined : { value: candidates[index] };
};

const checkOptions = (
  node: SuggestedNode,
  block: Block,
): GraphProblem | undefined => {
  for (const [option, value] of Object.entries(node.options ?? {})) {
    const config = block.options?.[option];
    // An option set by a sub-graph has no value of its own to set.
    if (
      !config ||
      config.conditions?.isHidden ||
      config.conditions?.isConnector
    ) {
      return { problem: 'unknownOption', nodeId: node.id, option };
    }
    if (!optionValue(config, value)) {
      return { problem: 'badOption', nodeId: node.id, option };
    }
  }
  return undefined;
};

/** Every node once, of a kind that exists, with options it has and values that fit. */
const checkNodes = (
  graph: SuggestedGraph,
  blocks: Blocks,
): GraphProblem | undefined => {
  const seen = new Set<string>();
  for (const node of graph.nodes) {
    if (seen.has(node.id) || node.id === OUTPUT)
      return { problem: 'duplicateNode', nodeId: node.id };
    seen.add(node.id);

    const block = blockFor(blocks, node.kind);
    if (!block) return { problem: 'unknownKind', nodeId: node.id };
    const problem = checkOptions(node, block);
    if (problem) return problem;
  }
  return undefined;
};

/**
 * Edges between nodes that exist, into inputs those nodes have. Each node goes
 * to one place, and an input takes one node unless it takes several.
 */
const checkEdges = (
  graph: SuggestedGraph,
  blocks: Blocks,
): GraphProblem | undefined => {
  const nodes = new Map(graph.nodes.map((node) => [node.id, node]));
  const placed = new Set<string>();
  const taken = new Set<string>();
  for (const edge of graph.edges) {
    if (!nodes.has(edge.from))
      return { problem: 'unknownNode', nodeId: edge.from };
    const to = nodes.get(edge.to);
    if (edge.to !== OUTPUT && !to)
      return { problem: 'unknownNode', nodeId: edge.to };
    const input = to && inputFor(blocks[to.kind], edge.port);
    if (to && !input) return { problem: 'unknownPort', nodeId: edge.to };
    if (placed.has(edge.from))
      return { problem: 'twoParents', nodeId: edge.from };
    placed.add(edge.from);

    const slot = JSON.stringify([edge.to, input?.id]);
    if (!input?.multi && taken.has(slot))
      return { problem: 'inputTaken', nodeId: edge.to };
    taken.add(slot);
  }
  return undefined;
};

/** Following each node's edge has to end at the output, not in a circle. */
const checkConnected = (graph: SuggestedGraph): GraphProblem | undefined => {
  // After checkEdges, each node has at most one edge: the one to its parent.
  const parents = new Map(graph.edges.map((edge) => [edge.from, edge.to]));
  for (const { id } of graph.nodes) {
    const seen = new Set<string>();
    let current: string | undefined = id;
    while (current !== undefined && current !== OUTPUT && !seen.has(current)) {
      seen.add(current);
      current = parents.get(current);
    }
    if (current !== OUTPUT) return { problem: 'notConnected', nodeId: id };
  }
  return undefined;
};

/**
 * Checks the assistant's graph against the node types, the way the editor
 * would let a user build it: known kinds and options, values that fit, edges
 * between nodes that exist and inputs they have, every node with one place to
 * go, and exactly one node on the output that everything leads to.
 */
export const checkGraph = (
  graph: SuggestedGraph,
  blocks: Blocks,
): GraphProblem | undefined => {
  const nodeProblem = checkNodes(graph, blocks);
  if (nodeProblem) return nodeProblem;
  if (graph.edges.filter((edge) => edge.to === OUTPUT).length !== 1)
    return { problem: 'noOutput' };
  return checkEdges(graph, blocks) ?? checkConnected(graph);
};

/**
 * The assistant's graph as the editor's nodes and edges: each node gets a new
 * id, its default options with the ones the assistant set, and each edge the
 * ports it connects. Only for a graph that passed checkGraph.
 */
/**
 * The node of the field's graph that a suggested node keeps: the one with its
 * id and kind. Kept, it stays the same node, with the options the assistant
 * leaves out, and the graphs of its options.
 */
const keptNode = (
  node: SuggestedNode,
  current: Graph | undefined,
): NodeInstance | undefined => {
  const existing = current?.nodes[node.id];
  return existing &&
    existing.id !== TERMINAL_NODE_ID &&
    existing.kind === node.kind
    ? existing
    : undefined;
};

export const graphParts = (
  graph: SuggestedGraph,
  blocks: Blocks,
  current?: Graph,
): ReplaceGraphCommandProps => {
  const ids = new Map(
    graph.nodes.map((node) => [
      node.id,
      keptNode(node, current)?.id ?? defaultIdFactory.nodeId(),
    ]),
  );
  const kinds = new Map(graph.nodes.map((node) => [node.id, node.kind]));

  const nodes = graph.nodes.map((node): NodeInstance => {
    const id = ids.get(node.id) as string;
    // checkGraph made sure the kind is one of the node types.
    const base =
      keptNode(node, current) ??
      createNodeInstance({ kind: node.kind as LogicBlockIndex, id }, blocks);
    const options = { ...base.options } as Record<string, OptionEntry<unknown>>;
    const block = blocks[node.kind];
    for (const [key, value] of Object.entries(node.options ?? {})) {
      const read = optionValue(block.options[key], value);
      // A value the assistant sets is typed in, like in the option's field.
      options[key] = {
        type: OptionType.UserDefined,
        value: read ? read.value : value,
        // A node saved before the option existed doesn't have it yet.
        visibility: options[key]?.visibility ?? OptionVisibility.Exposed,
      };
    }
    return { ...base, id, options } as NodeInstance;
  });

  const edges = graph.edges.map((edge) => ({
    from: {
      nodeId: ids.get(edge.from) as string,
      portId: outputOf(blocks[kinds.get(edge.from) as string]) as string,
    },
    to:
      edge.to === OUTPUT
        ? { nodeId: TERMINAL_NODE_ID, portId: inputsOf(blocks.terminal)[0].id }
        : {
            nodeId: ids.get(edge.to) as string,
            portId: (
              inputFor(blocks[kinds.get(edge.to) as string], edge.port) as Port
            ).id,
          },
  }));

  return { nodes, edges };
};

type Translate = (key: string) => string;

const optionText = (value: unknown) =>
  typeof value === 'string' ? `“${value}”` : JSON.stringify(value);

/** A node by the names the editor shows: its title, and the options that are set. */
const nodeText = (
  node: SuggestedNode,
  blocks: Blocks,
  translate: Translate,
  kept: boolean,
) => {
  const block = blocks[node.kind];
  const options = Object.entries(node.options ?? {}).map(([key, value]) => {
    const title = block?.options?.[key]?.title;
    return `${title ? translate(title) : key} ${optionText(value)}`;
  });
  const keeps = kept ? [translate('chat.change.keptNode')] : [];
  return [
    block ? translate(block.title) : node.kind,
    ...options,
    ...keeps,
  ].join(' · ');
};

/**
 * The graph as the card shows it, one node per line: from the node that gives
 * the field its value down to the nodes it reads, each a step further in.
 */
export const graphLines = (
  graph: SuggestedGraph,
  blocks: Blocks,
  translate: Translate,
  current?: Graph,
): { depth: number; text: string }[] => {
  const lines: { depth: number; text: string }[] = [];
  const seen = new Set<string>();
  const visit = (target: string, depth: number) => {
    for (const edge of graph.edges.filter((e) => e.to === target)) {
      const node = graph.nodes.find((n) => n.id === edge.from);
      // A graph that didn't pass the check may go round in a circle.
      if (!node || seen.has(node.id)) continue;
      seen.add(node.id);
      const kept = Boolean(keptNode(node, current));
      lines.push({ depth, text: nodeText(node, blocks, translate, kept) });
      visit(node.id, depth + 1);
    }
  };
  visit(OUTPUT, 0);
  return lines;
};

/** The names a problem's message uses: the node's title, its kind and id, and the option. */
export const problemNames = (
  graph: SuggestedGraph,
  { nodeId, option }: GraphProblem,
  blocks: Blocks,
  translate: Translate,
) => {
  const node = graph.nodes.find((n) => n.id === nodeId);
  const block = node ? blockFor(blocks, node.kind) : undefined;
  const optionTitle = option ? block?.options?.[option]?.title : undefined;
  return {
    id: nodeId,
    kind: node?.kind,
    node: block ? translate(block.title) : nodeId,
    option: optionTitle ? translate(optionTitle) : option,
  };
};
